import Cookies from 'js-cookie';

export type AnalyticsData = {
	user_id?: string;
	user_type?: string;
	login_state?: string;
	golden_id?: string;
	[key: string]: any;
};

export type LoginState =
	| 'anonymous'
	| 'persistent'
	| 'partially authenticated'
	| 'fully authenticated';

/** Last parsed cookie, keyed by its raw value. */
let parsedCookieCache: { raw: string; parsed: SecurityCookie } | null = null;

type SecurityCookie = {
	userId?: string;
	goldenIdString?: string;
	persisted?: boolean;
	authorized?: boolean;
	valid?: boolean;
	persistAt?: string;
};

const parseSecurityCookie = (encoded: string): SecurityCookie => {
	if (parsedCookieCache?.raw === encoded) {
		return parsedCookieCache.parsed;
	}

	const decoded =
		typeof atob === 'function' ? atob(encoded) : Buffer.from(encoded, 'base64').toString('utf-8');
	const parsed = JSON.parse(decoded);

	parsedCookieCache = { raw: encoded, parsed };

	return parsed;
};

/** Only upgrades login_state, callers seed the default. */
export const enrichUserFromSecurityCookie = (data: AnalyticsData): AnalyticsData => {
	try {
		const encoded = Cookies.get('FSSecurityMX');
		if (!encoded) return data;

		const sc = parseSecurityCookie(encoded);

		if (sc?.goldenIdString) {
			data.user_type = 'member';
			data.golden_id = sc.goldenIdString;
		}

		if (data.golden_id === 'na' || data.golden_id == null) {
			data.golden_id = sc?.goldenIdString || 'na';
		}

		if (sc?.persisted) {
			data.login_state = 'persistent';
		} else if (sc?.authorized) {
			data.login_state = 'partially authenticated';
		} else if (sc?.valid) {
			const expired = sc.persistAt && new Date(sc.persistAt).getTime() < Date.now();

			data.login_state = expired ? 'persistent' : 'fully authenticated';
		}
	} catch (error) {
		// Two calls because winston keeps only the first argument.
		console.error('Could not enrich analytics data from the FSSecurityMX cookie');
		console.error(error);
	}

	return data;
};

export const getLoginState = (): LoginState =>
	(enrichUserFromSecurityCookie({ login_state: 'anonymous' }).login_state as LoginState) ??
	'anonymous';

const getGoldenIdSync = (): string | null => {
	try {
		const encoded = Cookies.get('FSSecurityMX');
		if (!encoded) return null;

		const sc = parseSecurityCookie(encoded);
		const id = sc?.goldenIdString;
		return id ? id : null;
	} catch {
		return null;
	}
};

/** Changes whenever the guest does. Scopes the SWR cache and the frozen set. */
export const getGuestScope = (): string => getGoldenIdSync() ?? 'anonymous';
